/**
 * One reading, two audiences.
 *
 * A person wants one sentence and, if they want more, a picture. A clinician
 * wants to know which of the five gates failed and by how much. Serving both
 * from a single screen produced a compromise that under-served each: a table
 * of engine internals sitting under a drawer nobody opens, and a patient
 * sentence with no way to see the evidence behind it.
 *
 * So the reading now has two views. The patient view is the default and is
 * complete on its own — nothing is hidden behind the toggle that a person needs.
 * The clinician view is the same reading with the working shown.
 *
 * Both are driven from the identical result object. There is no second source
 * of truth, and neither view can say something the other contradicts.
 */

import { useState } from 'react';
import { MONK_SWATCHES, SCANNER_DEFAULTS, signalAttenuation } from '@omnialera/ori-signal';
import { renderPulseCard, sharePulseCard } from '../lib/pulsecard.js';
import { useT } from '../lib/i18n.jsx';
import { SignalFloor } from './spectrum.jsx';
import { Pill, Stat, percent } from './ui.jsx';

/** Region index → string key. Mirrors REGION_LAYOUT in lib/camera.js; an index
 *  outside it falls back to a numbered label rather than a missing string. */
const REGION_KEYS = ['clin.region.forehead', 'clin.region.leftCheek', 'clin.region.rightCheek'];

export function Reading({ result, tone, donateState, getSpectrum, live, scanning }) {
  const t = useT();
  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem('ori-view') === 'clinician' ? 'clinician' : 'patient';
    } catch {
      return 'patient';
    }
  });

  const choose = (next) => {
    setView(next);
    try {
      localStorage.setItem('ori-view', next);
    } catch {
      /* private window — the choice just does not persist */
    }
  };

  return (
    <div className="card">
      <div className="row">
        <p className="eyebrow" style={{ margin: 0 }}>
          {t('result.title')}
        </p>
        {result.locked ? (
          <Pill kind="ok">{t('result.found', { seconds: result.seconds.toFixed(1) })}</Pill>
        ) : (
          <Pill kind="bad">{t('result.none')}</Pill>
        )}
      </div>

      <div className="viewswitch" role="group" aria-label={t('view.group')}>
        <button
          type="button"
          className="viewbtn"
          aria-pressed={view === 'patient'}
          onClick={() => choose('patient')}
        >
          {t('view.patient')}
        </button>
        <button
          type="button"
          className="viewbtn"
          aria-pressed={view === 'clinician'}
          onClick={() => choose('clinician')}
        >
          {t('view.clinician')}
        </button>
      </div>

      {view === 'patient' ? (
        <PatientView result={result} tone={tone} getSpectrum={getSpectrum} scanning={scanning} />
      ) : (
        <ClinicianView result={result} tone={tone} getSpectrum={getSpectrum} live={live} scanning={scanning} />
      )}

      {donateState?.key ? (
        <p
          className="mono"
          style={{
            fontSize: '0.85rem',
            margin: '12px 0 0',
            color: donateState.kind === 'error' ? 'var(--pulse)' : 'var(--signal)',
          }}
        >
          {t(donateState.key, donateState.vars)}
        </p>
      ) : null}

      <ShareCard result={result} tone={tone} />
    </div>
  );
}

/* ── the person ─────────────────────────────────────────────────────── */

function PatientView({ result, tone, getSpectrum, scanning }) {
  const t = useT();
  const attenuation = Math.round(signalAttenuation(tone ?? 1) * 100);

  return (
    <>
      {result.locked ? (
        <>
          <div className="trio" style={{ marginTop: '14px' }}>
            <Stat label={t('stat.pulse')} value={Math.round(result.heartRateBpm)} unit={t('unit.bpm')} />
            <Stat
              label={t('stat.breathing')}
              value={result.breathsPerMinute ? Math.round(result.breathsPerMinute) : '—'}
              unit={t('unit.perMin')}
            />
            <Stat
              label={t('stat.variability')}
              value={result.variabilityMs ? Math.round(result.variabilityMs) : '—'}
              unit={t('unit.ms')}
            />
          </div>
          <p style={{ fontSize: '0.92rem', color: 'var(--muted)', margin: '12px 0 0', lineHeight: 1.6 }}>
            {t('result.confirmed', {
              regions: result.contributingRegions,
              methods: Object.keys(result.byMethod ?? {}).length,
              spread: Math.round(result.methodSpreadBpm ?? 0),
            })}
          </p>
        </>
      ) : (
        <p style={{ fontSize: '1rem', color: 'var(--ink-soft)', margin: '14px 0 0', lineHeight: 1.6 }}>
          {t('result.refused', { attenuation })}
        </p>
      )}

      <figure className="floorfig">
        <SignalFloor
          getSpectrum={getSpectrum}
          active={scanning}
          labelPeak={result.locked}
          strings={{ bpm: t('unit.bpm'), axis: t('floor.axis') }}
        />
        <figcaption>{result.locked ? t('floor.caption.locked') : t('floor.caption.refused')}</figcaption>
      </figure>

      {result.conventionalBpm !== null ? (
        <p style={{ fontSize: '0.95rem', color: 'var(--muted)', margin: '14px 0 0', lineHeight: 1.6 }}>
          {t('result.conventional', { bpm: Math.round(result.conventionalBpm) })}
          {result.locked ? t('result.conventional.locked') : t('result.conventional.failed')}
        </p>
      ) : null}
    </>
  );
}

/* ── the clinician ──────────────────────────────────────────────────── */

/**
 * The five gates, each with what was measured, what was required, and whether
 * it passed.
 *
 * This is the question a clinician actually has — not "how confident is it"
 * but "what stopped it" — and until now the app evaluated all five and then
 * reported none of them by name.
 */
function criteria(result, t) {
  const cfg = SCANNER_DEFAULTS;
  const n = (v, digits = 2) => (v === null || v === undefined ? null : Number(v).toFixed(digits));

  const rows = [
    {
      key: 'prominence',
      measured: n(result.prominence),
      required: `≥ ${cfg.prominenceThreshold.toFixed(1)}`,
      pass: result.prominence !== null && result.prominence >= cfg.prominenceThreshold,
    },
    {
      key: 'harmonic',
      measured: n(result.harmonic),
      required: `≥ ${cfg.harmonicThreshold.toFixed(1)}`,
      pass: result.harmonic !== null && result.harmonic >= cfg.harmonicThreshold,
    },
    {
      key: 'stability',
      measured: result.stabilityBpm === null ? null : `${n(result.stabilityBpm, 1)} ${t('unit.bpm')}`,
      required: `≤ ${cfg.stabilityBpm.toFixed(1)} ${t('unit.bpm')}`,
      pass: result.stabilityBpm !== null && result.stabilityBpm <= cfg.stabilityBpm,
    },
  ];

  // Agreement is only assessable with enough contributing regions. Showing a
  // pass for a criterion that was never tested would be the same class of lie
  // as reporting a rate that was never found.
  const assessable = (result.contributingRegions ?? 0) >= cfg.minRegionsForAgreement;
  rows.push({
    key: 'agreement',
    measured: assessable ? percent(result.agreement ?? 0) : null,
    required: `≥ ${percent(cfg.agreementThreshold)}`,
    pass: assessable ? (result.agreement ?? 0) >= cfg.agreementThreshold : null,
    note: assessable ? null : t('gate.notAssessed', { regions: result.contributingRegions ?? 0 }),
  });

  rows.push({
    key: 'methods',
    measured:
      result.methodSpreadBpm === null ? null : `${n(result.methodSpreadBpm, 1)} ${t('unit.bpm')}`,
    required: `≤ ${cfg.methodToleranceBpm.toFixed(1)} ${t('unit.bpm')}`,
    pass: result.methodsAgree === true,
  });

  return rows;
}

function ClinicianView({ result, tone, getSpectrum, live, scanning }) {
  const t = useT();
  const rows = criteria(result, t);
  const failed = rows.filter((r) => r.pass === false);
  const attenuation = Math.round(signalAttenuation(tone ?? 1) * 100);

  return (
    <>
      <p className="clinlede">
        {result.locked
          ? t('clin.locked', { bpm: Math.round(result.heartRateBpm), seconds: result.seconds.toFixed(1) })
          : failed.length === 0
            ? t('clin.refusedNoGate')
            : t('clin.refused', {
                gates: failed.map((r) => t(`gate.${r.key}`)).join(', '),
              })}
      </p>

      {/* Its own scroll container: without one the table paints past the card
          and widens the whole document, which is how a clinician view for
          three columns of numbers turns into a page that scrolls sideways. */}
      <div className="tscroll">
      <table className="gates diag">
        <thead>
          <tr>
            <th scope="col">{t('gate.header.criterion')}</th>
            <th scope="col">{t('gate.header.measured')}</th>
            <th scope="col">{t('gate.header.required')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className={r.pass === false ? 'gatefail' : r.pass === null ? 'gateskip' : ''}>
              <th scope="row">
                <span className="gatemark" aria-hidden="true">
                  {r.pass === true ? '✓' : r.pass === false ? '✗' : '–'}
                </span>
                {t(`gate.${r.key}`)}
                <span className="sr">
                  {r.pass === true ? t('gate.pass') : r.pass === false ? t('gate.fail') : t('gate.skip')}
                </span>
                {r.note ? <em className="gatenote">{r.note}</em> : null}
              </th>
              <td className="mono">{r.measured ?? '—'}</td>
              <td className="mono">{r.required}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      <figure className="floorfig">
        <SignalFloor
          getSpectrum={getSpectrum}
          active={scanning}
          strings={{ bpm: t('unit.bpm'), axis: t('floor.axis') }}
        />
        <figcaption>{result.locked ? t('floor.caption.locked') : t('floor.caption.refused')}</figcaption>
      </figure>

      {result.perRegion?.length ? (
        <div className="tscroll">
        <table className="diag">
          <thead>
            <tr>
              <th scope="col">{t('diag.method')}</th>
              <th scope="col">{t('clin.region')}</th>
              <th scope="col">{t('diag.reads')}</th>
              <th scope="col">{t('stat.clarity')}</th>
            </tr>
          </thead>
          <tbody>
            {result.perRegion.map((r, i) => (
              <tr key={`${r.method}-${r.region}-${i}`}>
                <td>{t(`diag.method.${r.method}`)}</td>
                <td>{t(REGION_KEYS[r.region] ?? 'clin.region.other', { n: r.region + 1 })}</td>
                <td className="mono">{r.bpm === null ? '—' : `${Math.round(r.bpm)} ${t('unit.bpm')}`}</td>
                <td className="mono">{r.prominence.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      ) : null}

      <dl className="kv">
        <div>
          <dt>{t('diag.framesUsed')}</dt>
          <dd className="mono">
            {result.samples} / {result.samples + result.rejectedFrames} (
            {percent(result.frameAcceptance ?? 1)})
          </dd>
        </div>
        <div>
          <dt>{t('diag.perfusion')}</dt>
          <dd className="mono">
            {result.perfusionIndex ? `${(result.perfusionIndex * 1000).toFixed(1)} ×10⁻³` : '—'}
          </dd>
        </div>
        <div>
          <dt>{t('clin.attenuation')}</dt>
          <dd className="mono">{attenuation}%</dd>
        </div>
        <div>
          <dt>{t('clin.engine')}</dt>
          <dd className="mono">{result.scannerVersion}</dd>
        </div>
      </dl>

      {result.conventionalBpm !== null ? (
        <p className="clinconv">
          {t('clin.conventional', { bpm: Math.round(result.conventionalBpm) })}
        </p>
      ) : null}

      <p className="clindisc">{t('clin.disclaimer')}</p>
    </>
  );
}

/* ── sharing ────────────────────────────────────────────────────────── */

/**
 * Offered for refusals as well as locks. A card that only exists when the
 * measurement succeeded would make the failure invisible again, which is the
 * exact habit this product was built to break.
 */
function ShareCard({ result, tone }) {
  const t = useT();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const share = async () => {
    setBusy(true);
    setStatus(null);
    try {
      const blob = await renderPulseCard(result, {
        monkTone: tone,
        swatch: MONK_SWATCHES[(tone ?? 1) - 1],
        strings: {
          pulse: t('card.pulse'),
          bpm: t('unit.bpm'),
          breathing: t('card.breathing'),
          perMin: t('unit.perMin'),
          variability: t('card.variability'),
          time: t('card.time'),
          regions: t('card.regions'),
          methods: t('card.methods'),
          tone: t('card.tone'),
          trusted: t('card.trusted'),
          honest: t('card.honest'),
          refusedLabel: t('card.refusedLabel'),
          refusedHeadline: t('card.refusedHeadline'),
          lockedBody: t('card.lockedBody'),
          refusedBody: t('card.refusedBody'),
          disclaimer: t('card.disclaimer'),
        },
      });
      const outcome = await sharePulseCard(blob);
      if (outcome === 'downloaded') setStatus(t('card.downloaded'));
    } catch {
      setStatus(t('card.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 'var(--s2)' }}>
      <button type="button" className="btn ghost" onClick={share} disabled={busy}>
        {busy ? t('card.sharing') : t('card.share')}
      </button>
      {status ? (
        <p className="mono" style={{ fontSize: '0.82rem', color: 'var(--faint)', margin: '8px 0 0' }} role="status">
          {status}
        </p>
      ) : null}
    </div>
  );
}
